import React, { useMemo } from 'react';
import { useReducedMotion } from '../hooks';
import { useRipples, RippleLayer } from './useRipples';
import './worlds.css';

/**
 * FloatingStars — a peaceful night field with a glowing crescent moon and a soft
 * halo. Stars drift and twinkle; tap one for a small burst of light. Bounded star
 * count, gentle motion, no flashing. Stars are buttons (keyboard-activatable).
 * Under reduced motion the drift stops; taps still sparkle.
 */
export const FloatingStars: React.FC = () => {
  const reduced = useReducedMotion();
  const { ripples, spawnAtElement } = useRipples();

  const stars = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const r = (n: number) => (Math.sin((i + 1) * n) + 1) / 2;
        return {
          id: i,
          left: `${Math.round(r(78.2) * 94) + 3}%`,
          top: `${Math.round(r(12.9) * 82) + 4}%`,
          size: 8 + Math.round(r(3.3) * 10),
          dur: `${3 + Math.round(r(5.1) * 4)}s`,
          delay: `${-(r(7.7) * 4).toFixed(2)}s`,
        };
      }),
    []
  );

  return (
    <div className={`mm-world mm-world--stars ${reduced ? 'is-still' : ''}`}>
      <span className="mm-stars__halo" aria-hidden="true" />
      <svg className="mm-stars__moon" viewBox="0 0 120 120" aria-hidden="true">
        <path d="M 58 24 A 34 34 0 1 0 58 96 A 26 26 0 1 1 58 24 Z" fill="#f4f0ff" opacity="0.92" />
      </svg>
      {stars.map((s) => (
        <button
          key={s.id}
          type="button"
          className="mm-star"
          aria-label="Light a star"
          onClick={(e) => spawnAtElement(e.currentTarget)}
          style={{ left: s.left, top: s.top, width: s.size, height: s.size, animationDuration: s.dur, animationDelay: s.delay }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2 L14 9 L21 9 L15.5 13.5 L17.5 21 L12 16.5 L6.5 21 L8.5 13.5 L3 9 L10 9 Z" fill="currentColor" />
          </svg>
        </button>
      ))}
      <RippleLayer ripples={ripples} />
      <p className="mm-world__hint">Tap a star to light it ✦</p>
    </div>
  );
};

export default FloatingStars;
